import { ConfigProvider, Modal, Space, Table, type TableColumnsType, Typography } from 'antd';
import { type ReactNode, useContext, useMemo } from 'react';
import AuthorizedComponent from '../../../../components/authorized-component';
import { AuthContext } from '../../../../contexts/auth-context-provider';
import { type Site, type SiteMenuOrder } from '../../../../entities';
import { Roles } from '../../../../types';
import {
  totalBeverages,
  totalMeals,
  totalPrice,
  totalSupplies,
} from '../../../../utils/order-utils';
import utils from '../../../../utils/utils';
import type SiteMenuBeverageOrder from '../../../../entities/site-menu-beverage-order';
import type SiteMenuSupplyOrder from '../../../../entities/site-menu-supply-order';

type BeverageOrSupplyOrder = SiteMenuBeverageOrder | SiteMenuSupplyOrder;
type SiteRow = Site & { key: string };

const ROLES_SEEING_PRICES = [Roles.ROLE_HHC_ADMIN, Roles.ROLE_CUSTOMER_ADMIN];

const priceColumns = [
  {
    title: 'Unit Price',
    dataIndex: 'unitPrice',
    width: 100,
    render: (value: number) => <Typography.Text>{utils.currencyFormat(value)}</Typography.Text>,
  },
  {
    title: 'Total Price',
    dataIndex: 'totalPrice',
    width: 100,
    render: (value: number) => (
      <Typography.Text strong>{utils.currencyFormat(value)}</Typography.Text>
    ),
  },
];

const routeColumn = {
  title: 'Route',
  dataIndex: ['route', 'name'],
  width: 100,
  render: (value: string | undefined) => value ?? 'N/A',
};

const quantityColumn = { title: 'Quantity', dataIndex: 'quantity', width: 100 };

const siteMenuOrderColumns: TableColumnsType<SiteMenuOrder> = [
  {
    title: 'Meal Category',
    render: (_, record) => (
      <Typography.Text>
        {record.scheduledMeal.mealCategory.name + (record.mainType ? ` (${record.mainType})` : '')}
      </Typography.Text>
    ),
  },
  routeColumn,
  quantityColumn,
];

const beverageAndSupplyColumns: TableColumnsType<BeverageOrSupplyOrder> = [
  {
    title: 'Beverage or Supply',
    render: (_, record) => (
      <Typography.Text>
        {'beverage' in record ? record.beverage.name : record.supply.name}
      </Typography.Text>
    ),
  },
  routeColumn,
  quantityColumn,
];

const siteColumns: TableColumnsType<SiteRow> = [
  { title: 'Name', dataIndex: 'name', key: 'name' },
  {
    title: 'Total Meals',
    render: (_, site) => <Typography.Text>{totalMeals(site.siteMenuOrders ?? [])}</Typography.Text>,
  },
  {
    title: 'Total Beverages & Extras',
    render: (_, site) => (
      <Typography.Text>
        {totalBeverages(site.siteMenuOrders ?? []) + totalSupplies(site.siteMenuOrders ?? [])}
      </Typography.Text>
    ),
  },
];

const sitePriceColumn = {
  title: 'Total Price',
  render: (_: unknown, site: SiteRow) => (
    <Typography.Text strong>
      {utils.currencyFormat(totalPrice(site.siteMenuOrders ?? []))}
    </Typography.Text>
  ),
};

const SiteOrdersDetail = ({ site, showPrices }: { site: Site; showPrices: boolean }): ReactNode => {
  const siteMenuOrders = site.siteMenuOrders ?? [];
  const beveragesAndSupplies: BeverageOrSupplyOrder[] = [
    ...siteMenuOrders.flatMap((smo) => smo.siteMenuBeverageOrders ?? []),
    ...siteMenuOrders.flatMap((smo) => smo.siteMenuSupplyOrders ?? []),
  ];
  return (
    <ConfigProvider renderEmpty={() => 'No orders'}>
      <Table
        columns={showPrices ? siteMenuOrderColumns.concat(priceColumns) : siteMenuOrderColumns}
        dataSource={siteMenuOrders}
        pagination={false}
        size="small"
        expandable={{
          expandedRowRender: (record) =>
            record.comment ? <Typography.Text>{record.comment}</Typography.Text> : undefined,
          rowExpandable: (record) => record.comment !== undefined && record.comment !== null,
        }}
      />
      {beveragesAndSupplies.length > 0 && (
        <Table
          columns={
            showPrices ? beverageAndSupplyColumns.concat(priceColumns) : beverageAndSupplyColumns
          }
          dataSource={beveragesAndSupplies}
          pagination={false}
          size="small"
        />
      )}
    </ConfigProvider>
  );
};

interface CustomerOrderSummaryModalProps {
  open: boolean;
  /** The sites of the order, each carrying its site menu orders in "siteMenuOrders" */
  sites: Site[];
  isUpdating: boolean;
  confirmLoading: boolean;
  onOk: () => void;
  onCancel: () => void;
}

const CustomerOrderSummaryModal = ({
  open,
  sites,
  isUpdating,
  confirmLoading,
  onOk,
  onCancel,
}: CustomerOrderSummaryModalProps): ReactNode => {
  const { session } = useContext(AuthContext);
  const showPrices = !!session && ROLES_SEEING_PRICES.includes(session.claims.role);

  const siteMenuOrders = useMemo(() => sites.flatMap((site) => site.siteMenuOrders ?? []), [sites]);
  // Rows are keyed by position, so the first site with orders is '0'
  const siteRows = useMemo<SiteRow[]>(
    () =>
      sites
        .filter((site) => (site.siteMenuOrders ?? []).length > 0)
        .map((site, index) => ({ ...site, key: String(index) })),
    [sites]
  );

  return (
    <Modal
      title={<Typography.Title level={2}>Sites order summary</Typography.Title>}
      open={open}
      onOk={onOk}
      confirmLoading={confirmLoading}
      onCancel={onCancel}
      width={1000}
      okText={isUpdating ? 'Update' : 'Submit'}
      destroyOnHidden
    >
      <Typography.Title level={3}>
        You are ordering a total of {totalMeals(siteMenuOrders)} items for these sites
      </Typography.Title>
      <Space direction="vertical">
        <Typography.Text>
          Total Meals: <strong>{totalMeals(siteMenuOrders)} meals</strong>
        </Typography.Text>
        <Typography.Text>
          Total Beverages: <strong>{totalBeverages(siteMenuOrders)} Beverages</strong>
        </Typography.Text>
        <Typography.Text>
          Total Supplies: <strong>{totalSupplies(siteMenuOrders)} Supplies</strong>
        </Typography.Text>
        <AuthorizedComponent roles={ROLES_SEEING_PRICES}>
          <Typography.Text>
            Total Price: <strong>{utils.currencyFormat(totalPrice(siteMenuOrders))}</strong>
          </Typography.Text>
        </AuthorizedComponent>
      </Space>
      <Typography.Title level={3}>Detailed orders for each site</Typography.Title>
      <ConfigProvider renderEmpty={() => 'No Orders'}>
        <Table
          style={{ marginTop: 20, marginBottom: 20 }}
          columns={showPrices ? siteColumns.concat([sitePriceColumn]) : siteColumns}
          expandable={{
            expandedRowRender: (site) => <SiteOrdersDetail site={site} showPrices={showPrices} />,
            defaultExpandedRowKeys: ['0'],
          }}
          dataSource={siteRows}
          size="small"
          pagination={false}
        />
      </ConfigProvider>
    </Modal>
  );
};

export default CustomerOrderSummaryModal;
